/**
 * NHS / CHI / UK postcode format checks. Optional at the Patient trust boundary
 * (empty is skipped by the controller); these functions treat empty as invalid.
 *
 * CHI: ISD Scotland — 10 digits, DDMMYY + serial, modulus-11 check (same weights as NHS).
 * Postcode: GOV.UK bulk-data / BS 7666 regex, GIR 0AA included, space optional.
 * Format only — no external lookups. A well-formed code (e.g. "L90SA") may not actually
 * be allocated by Royal Mail; that is out of scope here by design.
 */

const MODULUS11_WEIGHTS = [10, 9, 8, 7, 6, 5, 4, 3, 2];

const digitsOnly = (value) => (value ?? '').replace(/\s+/g, '').trim();

/** @returns {{ ok: true, check: number } | { ok: false, reason: string }} */
const modulus11Check = (digits10) => {
  const digits = [...digits10].map((c) => Number(c));
  const total = MODULUS11_WEIGHTS.reduce((sum, w, i) => sum + digits[i] * w, 0);
  let calculated = 11 - (total % 11);
  if (calculated === 11) calculated = 0;
  if (calculated === 10) return { ok: false, reason: 'invalid check digit (calculated as 10)' };
  return { ok: true, check: calculated, actual: digits[9] };
};

const isValidChiDob = (ddmmyy) => {
  const d = Number(ddmmyy.slice(0, 2));
  const m = Number(ddmmyy.slice(2, 4));
  const yy = Number(ddmmyy.slice(4, 6));
  if (m < 1 || m > 12) return false;
  const daysInMonth = (year) => new Date(year, m, 0).getDate();
  return d >= 1 && (d <= daysInMonth(1900 + yy) || d <= daysInMonth(2000 + yy));
};

const identifierResult = (kind, value, extra) => {
  const clean = digitsOnly(value);
  if (!clean) return { isValid: false, reason: `${kind} is empty or null` };
  if (clean.length !== 10) return { isValid: false, reason: `${kind} must be exactly 10 digits` };
  if (!/^\d{10}$/.test(clean)) return { isValid: false, reason: `${kind} must contain only digits` };
  const extraErr = extra?.(clean);
  if (extraErr) return extraErr;
  const check = modulus11Check(clean);
  if (!check.ok) {
    return { isValid: false, reason: `${kind} has invalid check digit (calculated as 10)` };
  }
  if (check.check !== check.actual) {
    return { isValid: false, reason: `Check digit mismatch: expected ${check.check}, got ${check.actual}` };
  }
  return { isValid: true, reason: `Valid ${kind}` };
};

export const validateNhsNumber = (value) => identifierResult('NHS number', value);

export const validateChiNumber = (value) =>
  identifierResult('CHI number', value, (clean) => {
    if (!isValidChiDob(clean.slice(0, 6))) {
      return { isValid: false, reason: 'CHI number date of birth (first 6 digits) is not a valid date' };
    }
    return null;
  });

// GOV.UK bulk data transfer additional validation (Nov 2015) — space optional.
const UK_POSTCODE =
  /^([Gg][Ii][Rr] ?0[Aa]{2}|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?)))) ?[0-9][A-Za-z]{2}))$/;

export const validateUkPostcode = (value) => {
  const clean = (value ?? '').trim().replace(/\s+/g, ' ');
  if (!clean) return { isValid: false, reason: 'Postcode is empty or null' };
  if (!UK_POSTCODE.test(clean)) return { isValid: false, reason: 'Postcode is not a valid UK postcode' };
  return { isValid: true, reason: 'Valid UK postcode' };
};

/** Ant Design Form.Item extra rule. Empty values pass (fields are optional). */
export const optionalIdentifierRule = (fieldName) => {
  const validators = {
    NHSNumber: validateNhsNumber,
    CHINumber: validateChiNumber,
    Postcode: validateUkPostcode,
  };
  const validate = validators[fieldName];
  if (!validate) return null;
  return {
    validator: (_, value) => {
      if (value === undefined || value === null || String(value).trim() === '') {
        return Promise.resolve();
      }
      const { isValid, reason } = validate(value);
      return isValid ? Promise.resolve() : Promise.reject(new Error(reason));
    },
  };
};
